import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet, Loader, CheckCircle2, AlertCircle, ChevronLeft,
  UploadCloud, Trash2, Link2, Plus, X, BarChart2, RefreshCw,
} from 'lucide-react';
import { parseWahrWorkbook } from '../utils/wahrParse';
import { IS_TENANT } from '../tenant/config';

const API = '/.netlify/functions/wahr-sb';
async function call(action, body = {}) {
  const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...body }) });
  const j = await res.json().catch(() => ({ success: false, error: 'Bad response' }));
  if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
  return j;
}

const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 16 };
const statBox = { background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '12px 14px', textAlign: 'center' };
const btn = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: bg, color: fg, border: 'none', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
const label = { fontSize: 11, fontWeight: 600, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' }) : '—');

export default function WahrImportPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [aliases, setAliases] = useState({});
  const [parsed, setParsed] = useState([]);   // [{name, rows, meta, warnings}]
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [err, setErr] = useState('');
  const [aliasFrom, setAliasFrom] = useState('');
  const [aliasTo, setAliasTo] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([call('getStats'), call('listAliases')]);
      setStats(s); setAliases(a.aliases || {});
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Pull the current in-progress pay periods from the live parcel_events feed
  // (same as the nightly auto-sync). Fills the newest FY column between WAHR files.
  const doSync = useCallback(async () => {
    setSyncing(true); setSyncMsg(''); setErr('');
    try {
      const res = await fetch('/.netlify/functions/wahr-sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const j = await res.json().catch(() => ({ ok: false, error: 'Bad response' }));
      if (!res.ok || j.ok === false) throw new Error(j.error || `HTTP ${res.status}`);
      setSyncMsg(j.synced ? `Synced ${(j.synced).toLocaleString()} rows from the live feed (${j.from} → ${j.to}).` : 'Already up to date.');
      await refresh();
    } catch (e) { setErr(e.message); } finally { setSyncing(false); }
  }, [refresh]);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList).filter((f) => /\.xlsx?$/i.test(f.name));
    if (!files.length) return;
    setParsing(true); setErr(''); setUploadMsg('');
    const out = [];
    for (const f of files) {
      try {
        const buf = await f.arrayBuffer();
        const { rows, meta, warnings } = parseWahrWorkbook(buf, f.name);
        out.push({ name: f.name, rows, meta, warnings });
      } catch (e) {
        out.push({ name: f.name, rows: [], meta: null, warnings: [`Parse failed: ${e.message}`] });
      }
    }
    out.sort((a, b) => (a.meta?.minDate || '').localeCompare(b.meta?.minDate || ''));
    setParsed(out); setParsing(false);
  }, []);

  const onDrop = useCallback((e) => { e.preventDefault(); if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files); }, [handleFiles]);

  const doUpload = useCallback(async () => {
    const good = parsed.filter((p) => p.rows.length);
    if (!good.length) return;
    setUploading(true); setErr(''); setUploadMsg('');
    let total = 0;
    try {
      for (const p of good) {
        setUploadMsg(`Importing ${p.name}…`);
        const r = await call('importRows', { rows: p.rows, sourceFile: p.name });
        total += r.written || 0;
      }
      setUploadMsg(`Imported ${total.toLocaleString()} driver-day rows from ${good.length} file(s).`);
      setParsed([]);
      await refresh();
    } catch (e) { setErr(e.message); } finally { setUploading(false); }
  }, [parsed, refresh]);

  const deleteFile = useCallback(async (name) => {
    if (!window.confirm(`Delete all imported rows from "${name}"? This can be re-imported.`)) return;
    try { await call('deleteFile', { sourceFile: name }); await refresh(); } catch (e) { setErr(e.message); }
  }, [refresh]);

  const addAlias = useCallback(async () => {
    const from = aliasFrom.trim().toUpperCase(); const to = aliasTo.trim().toUpperCase();
    if (!from || !to || from === to) return;
    try { const r = await call('saveAlias', { from, to }); setAliases(r.aliases || {}); setAliasFrom(''); setAliasTo(''); } catch (e) { setErr(e.message); }
  }, [aliasFrom, aliasTo]);
  const removeAlias = useCallback(async (from) => {
    try { const r = await call('deleteAlias', { from }); setAliases(r.aliases || {}); } catch (e) { setErr(e.message); }
  }, []);

  // Distinct logins across the pending batch, with their alias-resolved form.
  const batchLogins = React.useMemo(() => {
    const set = new Set();
    parsed.forEach((p) => (p.meta?.logins || []).forEach((l) => set.add(l)));
    return [...set].sort().map((l) => ({ login: l, resolved: aliases[l] || l, aliased: !!aliases[l] }));
  }, [parsed, aliases]);

  const totalPending = parsed.reduce((s, p) => s + p.rows.length, 0);
  const parcelsPending = parsed.reduce((s, p) => s + (p.meta?.totalParcels || 0), 0);

  return (
    <div style={{ padding: 32, maxWidth: 1000, margin: '0 auto' }}>
      <button onClick={() => navigate(-1)} style={{ ...btn('transparent', 'var(--hp-dim)'), padding: '4px 0', marginBottom: 8 }}>
        <ChevronLeft size={16} /> Back
      </button>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Import WAHR History</h1>
          <p style={{ color: 'var(--hp-dim)', fontSize: 13, margin: '6px 0 0', maxWidth: 620 }}>
            Drop the monthly WAHR workbooks (one per period). Parcels are read from the
            <strong> Data (WAHR) Column&nbsp;J</strong> total; hours from the
            <strong> Activity Summary</strong> (Portal &amp; Scanner). Re-importing a month is safe — rows
            upsert on date&nbsp;+&nbsp;driver.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Live sync reads our parcel_events feed — a tenant deployment has none. */}
            {!IS_TENANT && (
            <button onClick={doSync} disabled={syncing} title="Pull the current in-progress period from the live feed" style={{ ...btn('transparent', 'var(--hp-accent)'), border: '1px solid var(--hp-border)' }}>
              {syncing ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={16} />} Sync live now
            </button>
            )}
            <button onClick={() => navigate('/wahr-comparison')} style={btn('var(--hp-accent)', '#fff')}>
              <BarChart2 size={16} /> Comparison
            </button>
          </div>
          {syncMsg && <span style={{ fontSize: 12, color: '#22c55e' }}>{syncMsg}</span>}
        </div>
      </div>

      {err && (
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, borderColor: 'rgba(239,68,68,0.4)', color: '#ef4444', fontSize: 13 }}>
          <AlertCircle size={16} /> {err}
        </div>
      )}

      {/* Coverage */}
      <div style={card}>
        <div style={{ ...label, marginBottom: 12 }}>Current coverage</div>
        {!stats ? <div style={{ color: 'var(--hp-dim)', fontSize: 13 }}><Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
          : stats.rowCount === 0 ? <div style={{ color: 'var(--hp-dim)', fontSize: 13 }}>No WAHR history imported yet.</div>
            : (
              <>
                <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
                  {[
                    ['Driver-days', stats.rowCount.toLocaleString()],
                    ['Parcels', (stats.parcels || 0).toLocaleString()],
                    ['Drivers', stats.driverCount],
                    ['Date span', `${fmtDate(stats.minDate)} – ${fmtDate(stats.maxDate)}`],
                  ].map(([l, v]) => (
                    <div key={l} style={statBox}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--hp-accent)' }}>{v}</div>
                      <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 2 }}>{l}</div>
                    </div>
                  ))}
                </div>
                <div style={{ ...label, marginBottom: 6 }}>Imported files</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {stats.files.map((f) => (
                    <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, padding: '4px 0' }}>
                      <FileSpreadsheet size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
                      <span style={{ flex: 1 }}>{f.name}</span>
                      <span style={{ color: 'var(--hp-dim)' }}>{f.count.toLocaleString()} rows</span>
                      <button onClick={() => deleteFile(f.name)} title="Delete this import" style={{ ...btn('transparent', '#ef4444'), padding: 4 }}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </>
            )}
      </div>

      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileRef.current?.click()}
        style={{ ...card, border: '2px dashed var(--hp-border)', textAlign: 'center', padding: '36px 20px', cursor: 'pointer' }}
      >
        <FileSpreadsheet size={40} style={{ color: 'var(--hp-accent)', marginBottom: 12 }} />
        <p style={{ fontWeight: 600, margin: '0 0 4px', fontSize: 15 }}>{parsing ? 'Parsing…' : 'Drop WAHR .xlsx files here'}</p>
        <p style={{ color: 'var(--hp-dim)', fontSize: 13, margin: 0 }}>or click to browse — you can select several at once</p>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" multiple style={{ display: 'none' }}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {/* Pending preview */}
      {parsed.length > 0 && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ ...label }}>Ready to import — {parsed.length} file(s), {totalPending.toLocaleString()} driver-days, {parcelsPending.toLocaleString()} parcels</div>
            <button onClick={() => setParsed([])} style={{ ...btn('transparent', 'var(--hp-dim)'), marginLeft: 'auto', padding: 4 }}><X size={16} /></button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginBottom: 14 }}>
            <thead>
              <tr>{['File', 'Period', 'Driver-days', 'Drivers', 'Parcels', ''].map((h) => (
                <th key={h} style={{ textAlign: h === 'File' ? 'left' : 'right', padding: '6px 10px', borderBottom: '1px solid var(--hp-border)', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {parsed.map((p) => (
                <tr key={p.name}>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--hp-border)', fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', whiteSpace: 'nowrap' }}>{p.meta ? `${fmtDate(p.meta.minDate)} – ${fmtDate(p.meta.maxDate)}` : '—'}</td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{p.rows.length.toLocaleString()}</td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{p.meta?.driverCount ?? '—'}</td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 700 }}>{(p.meta?.totalParcels || 0).toLocaleString()}</td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>
                    {p.warnings.length > 0
                      ? <span title={p.warnings.join('\n')} style={{ color: '#d97706', display: 'inline-flex', alignItems: 'center', gap: 4 }}><AlertCircle size={13} /> {p.warnings.length}</span>
                      : <CheckCircle2 size={14} style={{ color: '#22c55e' }} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {parsed.some((p) => p.warnings.length > 0) && (
            <div style={{ fontSize: 12, color: '#d97706', marginBottom: 12 }}>
              {parsed.flatMap((p) => p.warnings.map((w) => `${p.name}: ${w}`)).map((w) => <div key={w}>⚠ {w}</div>)}
            </div>
          )}
          <button onClick={doUpload} disabled={uploading || !totalPending} style={{ ...btn('#4f46e5', '#fff'), opacity: uploading || !totalPending ? 0.6 : 1 }}>
            {uploading ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> {uploadMsg || 'Importing…'}</> : <><UploadCloud size={16} /> Import {totalPending.toLocaleString()} rows</>}
          </button>
        </div>
      )}

      {uploadMsg && !uploading && parsed.length === 0 && (
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, borderColor: 'rgba(34,197,94,0.4)', color: '#22c55e', fontSize: 13 }}>
          <CheckCircle2 size={16} /> {uploadMsg}
        </div>
      )}

      {/* Batch logins + aliases */}
      {batchLogins.length > 0 && (
        <div style={card}>
          <div style={{ ...label, marginBottom: 10 }}>Logins in this batch ({batchLogins.length}) — check for renamed personnel</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {batchLogins.map((b) => (
              <span key={b.login} style={{ fontSize: 11.5, padding: '3px 8px', borderRadius: 12, border: '1px solid var(--hp-border)', background: b.aliased ? 'rgba(99,102,241,0.1)' : 'var(--hp-bg)', color: b.aliased ? 'var(--hp-accent)' : 'var(--hp-text)' }}>
                {b.aliased ? <>{b.login} → {b.resolved}</> : b.login}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={card}>
        <div style={{ ...label, marginBottom: 4 }}><Link2 size={12} style={{ verticalAlign: -1 }} /> Login aliases</div>
        <p style={{ color: 'var(--hp-dim)', fontSize: 12, margin: '0 0 12px' }}>
          Map an old/renamed login to the current person so year-over-year lines up. Applied on import.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <input value={aliasFrom} onChange={(e) => setAliasFrom(e.target.value)} placeholder="OLD LOGIN"
            style={{ padding: '8px 10px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontSize: 13, textTransform: 'uppercase', width: 150 }} />
          <span style={{ color: 'var(--hp-dim)' }}>→</span>
          <input value={aliasTo} onChange={(e) => setAliasTo(e.target.value)} placeholder="CURRENT LOGIN"
            style={{ padding: '8px 10px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontSize: 13, textTransform: 'uppercase', width: 150 }} />
          <button onClick={addAlias} style={btn('var(--hp-accent)', '#fff')}><Plus size={14} /> Add</button>
        </div>
        {Object.keys(aliases).length === 0 ? <div style={{ color: 'var(--hp-dim)', fontSize: 12.5 }}>No aliases yet.</div>
          : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {Object.entries(aliases).map(([from, to]) => (
                <span key={from} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, padding: '3px 6px 3px 10px', borderRadius: 12, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)' }}>
                  {from} → {to}
                  <button onClick={() => removeAlias(from)} style={{ ...btn('transparent', '#ef4444'), padding: 2 }}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
